import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileDown, AlertTriangle, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { downloadTrf16, getTrf16Readiness } from '@/lib/api';
import { usePolledData } from '@/lib/usePolledData';
import { showErrorToast } from '@/lib/toast';
import type {
  FideExportReadinessResponse,
  FideReadinessIssue,
} from '@swiss-magic/shared-types';
import { useHubOutletContext } from './TournamentHubLayoutRoute';

// Story 097 — FIDE TRF16 export. Chess-only hub sub-page. The download is
// always available (decision: arbiters judge readiness themselves); the panel
// surfaces data gaps by severity. Column-correct formatting lives on the
// backend (trf16-writer.ts); this page is the readiness UI + download trigger.

function issueLabel(
  issue: FideReadinessIssue,
  fieldName: (field: string) => string,
): string {
  return fieldName(issue.field);
}

export function FideExportPage() {
  const { t } = useTranslation('director');
  const { tournament } = useHubOutletContext();
  const [downloading, setDownloading] = useState(false);
  const tournamentId = tournament?.id;

  const { data, loading } = usePolledData<FideExportReadinessResponse>(
    () => getTrf16Readiness(tournamentId as string),
    [tournamentId],
  );

  if (!tournament) {
    return (
      <div className="text-sm text-muted-foreground">
        {t('tournament_hub.loading')}
      </div>
    );
  }

  const fieldName = (field: string) =>
    t(`fide_export.fields.${field}`, { defaultValue: field });

  async function handleDownload() {
    if (!tournamentId) return;
    setDownloading(true);
    try {
      await downloadTrf16(tournamentId);
    } catch {
      showErrorToast(t('fide_export.download_failed'));
    } finally {
      setDownloading(false);
    }
  }

  const errors = data?.issues.filter((i) => i.severity === 'error') ?? [];
  const warnings = data?.issues.filter((i) => i.severity === 'warning') ?? [];

  return (
    <div data-testid="fide-export-page" className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold md:text-2xl">
          {t('fide_export.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('fide_export.subtitle')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          data-testid="download-trf16-button"
          onClick={handleDownload}
          disabled={downloading}
        >
          <FileDown className="mr-2 h-4 w-4" />
          {downloading ? t('fide_export.generating') : t('fide_export.download_button')}
        </Button>
        {data && (
          <span className="text-sm text-muted-foreground">
            {t('fide_export.player_count', { count: data.playerCount })}
          </span>
        )}
      </div>

      <Card data-testid="fide-readiness-panel" className="p-4">
        <h3 className="mb-3 text-base font-medium">
          {t('fide_export.readiness_title')}
        </h3>

        {loading && !data && (
          <p className="text-sm text-muted-foreground">{t('fide_export.checking')}</p>
        )}

        {data && errors.length === 0 && warnings.length === 0 && (
          <p
            data-testid="fide-readiness-clear"
            className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400"
          >
            <CheckCircle2 className="h-4 w-4" />
            {t('fide_export.readiness_clear')}
          </p>
        )}

        {errors.length > 0 && (
          <div className="mb-4">
            <p className="mb-1 flex items-center gap-2 text-sm font-medium text-destructive">
              <AlertCircle className="h-4 w-4" />
              {t('fide_export.errors_heading')}
            </p>
            <ul className="space-y-1">
              {errors.map((issue, i) => (
                <li
                  key={`e-${i}`}
                  data-testid="fide-readiness-issue-error"
                  className="text-sm text-muted-foreground"
                >
                  {issue.playerName
                    ? `${issue.playerName} — ${issueLabel(issue, fieldName)}`
                    : issueLabel(issue, fieldName)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {warnings.length > 0 && (
          <div>
            <p className="mb-1 flex items-center gap-2 text-sm font-medium text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" />
              {t('fide_export.warnings_heading')}
            </p>
            <ul className="space-y-1">
              {warnings.map((issue, i) => (
                <li
                  key={`w-${i}`}
                  data-testid="fide-readiness-issue-warning"
                  className="text-sm text-muted-foreground"
                >
                  {issue.playerName
                    ? `${issue.playerName} — ${issueLabel(issue, fieldName)}`
                    : issueLabel(issue, fieldName)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
}
